import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import { Feature, ReachProSupportClient } from 'src/WebApiController';

export interface CreateConversationParams {
  topic: string;
  eventId: number;
  listingId?: number;
  saleId?: number;
  section?: string;
  row?: string;
  seatFrom?: string;
  seatTo?: string;
}

export function useReachProCreateConversation() {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const queryClient = useQueryClient();
  const hasReachProSupportFeature = useUserHasFeature(Feature.ReachProSupport);

  return useMutation({
    mutationFn: async (params: CreateConversationParams) => {
      if (!hasReachProSupportFeature) {
        throw new Error('Support Center feature is not enabled');
      }

      return tryInvokeApi(
        async () => {
          return await new ReachProSupportClient(
            activeAccountWebClientConfig
          ).createConversation({
            topic: params.topic,
            eventId: params.eventId,
            listingId: params.listingId ?? null,
            saleId: params.saleId ?? null,
            section: params.section ?? null,
            row: params.row ?? null,
            seatFrom: params.seatFrom ?? null,
            seatTo: params.seatTo ?? null,
          });
        },
        (error) => {
          trackError('ReachProSupportClient.createConversation', error);
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reachpro-conversations'] });
    },
  });
}
